/**
 * DiffViewer component.
 * Displays draft vs live config diff with line-level highlighting.
 * Uses a simple line-by-line comparison without external diff libraries.
 */

import type { ReactElement } from "react";
import { useMemo } from "react";

// ============================================================================
// Types
// ============================================================================

/** Props for DiffViewer component */
export interface DiffViewerProps {
  /** Draft content as JSON string */
  readonly draftContent: string;
  /** Live content as JSON string */
  readonly liveContent: string;
  /** Draft version number */
  readonly draftVersion?: number | undefined;
  /** Live version number */
  readonly liveVersion?: number | null | undefined;
}

/** Diff line type */
type DiffLineType = "unchanged" | "added" | "removed";

/** Single diff line */
interface DiffLine {
  readonly type: DiffLineType;
  readonly content: string;
  readonly lineNumberLeft: number | null;
  readonly lineNumberRight: number | null;
}

// ============================================================================
// Diff Algorithm
// ============================================================================

/**
 * Compute a simple line-by-line diff between two strings.
 * Uses a longest common subsequence (LCS) approach for accurate results.
 */
function computeLineDiff(original: string, modified: string): readonly DiffLine[] {
  const originalLines = original.split("\n");
  const modifiedLines = modified.split("\n");

  // Build LCS table
  const m = originalLines.length;
  const n = modifiedLines.length;
  const lcs: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0) as number[]);

  /** Safe LCS table access — returns 0 for out-of-bounds */
  function lcsAt(row: number, col: number): number {
    return lcs[row]?.[col] ?? 0;
  }

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const row = lcs[i];
      if (!row) continue;
      if (originalLines[i - 1] === modifiedLines[j - 1]) {
        row[j] = lcsAt(i - 1, j - 1) + 1;
      } else {
        row[j] = Math.max(lcsAt(i - 1, j), lcsAt(i, j - 1));
      }
    }
  }

  // Backtrack to produce diff
  const result: DiffLine[] = [];
  let i = m;
  let j = n;
  const stack: DiffLine[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && originalLines[i - 1] === modifiedLines[j - 1]) {
      stack.push({
        type: "unchanged",
        content: originalLines[i - 1] ?? "",
        lineNumberLeft: i,
        lineNumberRight: j,
      });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || lcsAt(i, j - 1) >= lcsAt(i - 1, j))) {
      stack.push({
        type: "added",
        content: modifiedLines[j - 1] ?? "",
        lineNumberLeft: null,
        lineNumberRight: j,
      });
      j--;
    } else {
      stack.push({
        type: "removed",
        content: originalLines[i - 1] ?? "",
        lineNumberLeft: i,
        lineNumberRight: null,
      });
      i--;
    }
  }

  // Reverse since we built it backwards
  while (stack.length > 0) {
    const item = stack.pop();
    if (item) result.push(item);
  }

  return result;
}

// ============================================================================
// Component
// ============================================================================

/**
 * DiffViewer displays a side-by-side diff of draft vs live config content.
 */
export function DiffViewer({
  draftContent,
  liveContent,
  draftVersion,
  liveVersion,
}: DiffViewerProps): ReactElement {
  const diffLines = useMemo(
    () => computeLineDiff(liveContent, draftContent),
    [liveContent, draftContent]
  );

  const stats = useMemo(() => {
    let added = 0;
    let removed = 0;
    for (const line of diffLines) {
      if (line.type === "added") added++;
      if (line.type === "removed") removed++;
    }
    return { added, removed };
  }, [diffLines]);

  const hasChanges = stats.added > 0 || stats.removed > 0;

  return (
    <div className="diff-viewer flex flex-col h-full" data-testid="diff-viewer">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-4 py-2">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Live{" "}
            {liveVersion !== null && liveVersion !== undefined ? (
              <span className="text-blue-600 dark:text-blue-400">(v{liveVersion})</span>
            ) : (
              <span className="text-gray-400 dark:text-gray-500">(none)</span>
            )}
          </span>
          <span className="text-gray-400">vs</span>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Draft{" "}
            {draftVersion !== undefined && (
              <span className="text-green-600 dark:text-green-400">(v{draftVersion})</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          {stats.added > 0 && (
            <span className="text-green-600 dark:text-green-400">+{stats.added} added</span>
          )}
          {stats.removed > 0 && (
            <span className="text-red-600 dark:text-red-400">-{stats.removed} removed</span>
          )}
          {!hasChanges && <span className="text-gray-500 dark:text-gray-400">No changes</span>}
        </div>
      </div>

      {/* Diff content */}
      <div className="flex-1 overflow-auto">
        {!hasChanges ? (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <p className="text-sm">No differences found between live and draft</p>
          </div>
        ) : (
          <table className="w-full text-xs font-mono" data-testid="diff-table">
            <tbody>
              {diffLines.map((line, idx) => (
                <DiffLineRow key={idx} line={line} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface DiffLineRowProps {
  readonly line: DiffLine;
}

function DiffLineRow({ line }: DiffLineRowProps): ReactElement {
  const bgColor = {
    unchanged: "",
    added: "bg-green-50 dark:bg-green-900/20",
    removed: "bg-red-50 dark:bg-red-900/20",
  }[line.type];

  const textColor = {
    unchanged: "text-gray-800 dark:text-gray-200",
    added: "text-green-800 dark:text-green-200",
    removed: "text-red-800 dark:text-red-200",
  }[line.type];

  const gutterColor = {
    unchanged: "text-gray-400 dark:text-gray-600",
    added: "text-green-500 dark:text-green-400",
    removed: "text-red-500 dark:text-red-400",
  }[line.type];

  const prefix = {
    unchanged: " ",
    added: "+",
    removed: "-",
  }[line.type];

  return (
    <tr className={bgColor}>
      <td
        className={`w-12 text-right px-2 py-0 select-none border-r border-gray-200 dark:border-gray-700 ${gutterColor}`}
      >
        {line.lineNumberLeft ?? ""}
      </td>
      <td
        className={`w-12 text-right px-2 py-0 select-none border-r border-gray-200 dark:border-gray-700 ${gutterColor}`}
      >
        {line.lineNumberRight ?? ""}
      </td>
      <td className={`w-5 text-center py-0 select-none ${gutterColor} font-bold`}>{prefix}</td>
      <td className={`py-0 px-2 whitespace-pre ${textColor}`}>{line.content}</td>
    </tr>
  );
}

export default DiffViewer;
